
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { useState } from 'react';
import { Loader2, AlertTriangle, ExternalLink, Calendar, Info } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { Badge } from '@/components/ui/badge';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';

interface UpcomingSessionsProps {
  sessions: any[];
  onCancelSession: (sessionId: string) => void;
  onJoinMeeting: (meetingLink: string | null, e: React.MouseEvent) => void;
}

export const UpcomingSessions = ({ 
  sessions,
  onCancelSession,
  onJoinMeeting
}: UpcomingSessionsProps) => {
  const [sessionToCancel, setSessionToCancel] = useState<string | null>(null);
  const [isCancelling, setIsCancelling] = useState(false);
  const { toast } = useToast();

  const handleCancelSession = async () => {
    if (!sessionToCancel) return;
    
    setIsCancelling(true);
    try {
      await onCancelSession(sessionToCancel);
    } finally {
      setIsCancelling(false);
      setSessionToCancel(null);
    }
  };

  const handleJoinMeeting = (meetingLink: string | null, meetUnavailable: boolean | undefined, e: React.MouseEvent) => {
    if (!meetingLink) {
      e.preventDefault();
      toast({
        title: "Meeting Link Unavailable",
        description: "No meeting link has been provided for this session.",
        variant: "destructive",
      });
      return;
    }
    
    if (meetUnavailable) {
      e.preventDefault();
      toast({
        title: "Google Meet Unavailable",
        description: "Google Meet is not enabled on the host's account. Please contact support or use an alternative video conferencing solution.",
        variant: "destructive",
      });
      return;
    }
    
    onJoinMeeting(meetingLink, e);
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    
    // Check if date is today
    const today = new Date();
    if (date.toDateString() === today.toDateString()) {
      return `Today at ${date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
    }
    
    // Check if date is tomorrow
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    if (date.toDateString() === tomorrow.toDateString()) {
      return `Tomorrow at ${date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
    }
    
    // Otherwise, show full date
    return `${date.toLocaleDateString()} at ${date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
  };

  const getMeetingStatus = (session: any) => {
    const now = new Date();
    const startTime = new Date(session.session.start_time);
    const endTime = new Date(session.session.end_time);
    
    if (now > endTime) {
      return { status: 'ended', label: 'Meeting Ended', variant: 'outline' as const };
    }
    
    if (now >= startTime && now <= endTime) {
      return { status: 'active', label: 'Join Now', variant: 'default' as const };
    }
    
    // Calculate time until meeting
    const diffMs = startTime.getTime() - now.getTime();
    const diffMins = Math.round(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    
    if (diffHours < 1) {
      return { 
        status: 'soon', 
        label: `Starting in ${diffMins} minute${diffMins !== 1 ? 's' : ''}`, 
        variant: 'secondary' as const 
      };
    }
    
    return { status: 'scheduled', label: 'View Meeting Details', variant: 'outline' as const };
  };

  const getMeetingLinkStatus = (session: any) => {
    if (session.error_details?.message?.includes("The OAuth client was not found")) {
      return {
        type: 'oauth_error',
        label: 'OAuth configuration error',
        description: 'The Google OAuth client was not found. Your Google client ID may be incorrect or the OAuth client has been deleted from Google Cloud Console.'
      };
    }
    
    if (!session.google_meet_link) {
      return { 
        type: 'missing', 
        label: 'No meeting link', 
        description: 'No Google Meet link was created for this session. Contact support for assistance.'
      };
    }
    
    if (session.meet_unavailable) {
      return {
        type: 'unavailable',
        label: 'Google Meet unavailable',
        description: 'Google Meet is not enabled on the host account. Use an alternative conferencing solution.'
      };
    }
    
    if (session.fallback) {
      return {
        type: 'temporary',
        label: 'Using temporary link',
        description: 'Using a temporary Google Meet link. This may require Google Workspace to function properly.'
      };
    }
    
    return {
      type: 'normal',
      label: 'Google Meet ready',
      description: 'A Google Meet link has been properly configured for this session.'
    };
  };

  return (
    <>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {sessions.map((session) => {
          const meetingStatus = getMeetingStatus(session);
          const meetingLinkStatus = getMeetingLinkStatus(session);
          const isLive = meetingStatus.status === 'active';
          const isEnded = meetingStatus.status === 'ended';
          const isSoon = meetingStatus.status === 'soon';

          return (
            <Card key={session.id} className={`flex flex-col ${isLive ? 'border-primary' : ''}`}>
              <CardHeader>
                <CardTitle className="flex justify-between items-start">
                  <div>
                    {session.session.title || `Session with ${session.session.host.username}`}
                    {isLive && <Badge className="ml-2 bg-green-500">Live Now</Badge>}
                    {isSoon && <Badge variant="secondary" className="ml-2">Starting Soon</Badge>}
                  </div>
                </CardTitle>
              </CardHeader>
              <CardContent className="flex flex-col flex-grow">
                <div className="flex-grow space-y-4">
                  <p className="text-sm text-gray-500 flex items-center">
                    <Calendar className="mr-2 h-4 w-4" />
                    {formatDate(session.session.start_time)}
                  </p>
                  <p className="text-sm text-gray-500">
                    Duration: {
                      Math.round(
                        (new Date(session.session.end_time).getTime() - 
                         new Date(session.session.start_time).getTime()) / 
                        (1000 * 60)
                      )
                    } minutes
                  </p>
                  <div className="flex items-center gap-2 mt-2">
                    <Avatar className="h-6 w-6">
                      <AvatarImage src={session.session.host.avatar_url || undefined} />
                      <AvatarFallback>
                        {session.session.host.username?.[0]?.toUpperCase() || '?'}
                      </AvatarFallback>
                    </Avatar>
                    <span className="text-sm text-gray-600">
                      with {session.session.host.username}
                    </span>
                  </div>
                  
                  {meetingLinkStatus.type !== 'normal' && (
                    <div className="flex items-center text-amber-600 text-xs mt-1">
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <div className="flex items-center">
                              {(meetingLinkStatus.type === 'unavailable' || meetingLinkStatus.type === 'oauth_error') ? (
                                <AlertTriangle className="h-4 w-4 mr-1" />
                              ) : (
                                <Info className="h-4 w-4 mr-1" />
                              )}
                              <span>{meetingLinkStatus.label}</span>
                            </div>
                          </TooltipTrigger>
                          <TooltipContent>
                            <p className="max-w-xs">
                              {meetingLinkStatus.description}
                            </p>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    </div>
                  )}
                </div>
                <div className="mt-4 border-t pt-4 space-y-2">
                  <Button
                    onClick={(e) => handleJoinMeeting(session.google_meet_link || null, session.meet_unavailable, e)}
                    variant={meetingStatus.variant}
                    className={`w-full ${isLive ? 'animate-pulse bg-green-600 hover:bg-green-700' : ''} ${session.meet_unavailable ? 'bg-amber-600 hover:bg-amber-700' : ''}`}
                    disabled={isEnded}
                  >
                    {isLive ? (
                      <div className="flex items-center">
                        <span className="relative flex h-3 w-3 mr-2">
                          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
                          <span className="relative inline-flex rounded-full h-3 w-3 bg-red-500"></span>
                        </span>
                        Join Meeting Now
                        <ExternalLink className="h-4 w-4 ml-2" />
                      </div>
                    ) : session.meet_unavailable ? (
                      <div className="flex items-center">
                        <AlertTriangle className="h-4 w-4 mr-2" />
                        Google Meet Unavailable
                      </div>
                    ) : (
                      <div className="flex items-center">
                        {meetingStatus.label}
                        {!isEnded && <ExternalLink className="h-4 w-4 ml-2" />}
                      </div>
                    )}
                  </Button>
                  <Button 
                    onClick={() => setSessionToCancel(session.id)} 
                    variant="destructive" 
                    className="w-full"
                    disabled={isEnded}
                  >
                    Cancel Session
                  </Button>
                </div>
              </CardContent>
            </Card>
          );
        })}
        {sessions.length === 0 && (
          <p className="text-gray-500">No upcoming sessions.</p>
        )}
      </div>

      <AlertDialog open={!!sessionToCancel} onOpenChange={(open) => !open && setSessionToCancel(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure you want to cancel this session?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. The host will be notified of your cancellation.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isCancelling}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleCancelSession} disabled={isCancelling}>
              {isCancelling ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Cancelling...
                </>
              ) : (
                'Confirm Cancellation'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};
